import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useAuth } from '../hooks/useAuth'
import PhotoLightbox from '../components/PhotoLightbox'
import TicketActivity from '../components/TicketActivity'

const TIER_STYLE = {
  'Critical': { bg: '#FCEBEB', color: '#A32D2D', dot: '🔴' },
  'High':     { bg: '#FAEEDA', color: '#854F0B', dot: '🟡' },
  'Medium':   { bg: '#EAF3DE', color: '#3B6D11', dot: '🟢' },
  'Low':      { bg: '#F1EFE8', color: '#5F5E5A', dot: '⚪' },
  'AMP':      { bg: '#F3E8FF', color: '#6B21A8', dot: '🟣' },
}

const STATUSES = ['open','scheduled','dispatched','remote','in_progress','blocked','resolved','cancelled']

// Keep this list in sync with TEAM_OPTIONS in Tickets.js NewTicketModal.
const TEAM_OPTIONS = ['field_service','remote_team','migration_pm','ncs','nxt_product','ops']

export default function TicketDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { profile } = useAuth()
  const [ticket, setTicket] = useState(null)
  const [profiles, setProfiles] = useState([])
  const [mentionableProfiles, setMentionableProfiles] = useState([])  // includes auditors etc.
  const [images, setImages] = useState([])              // [{ id, url, uploaded_by, storage_path, created_at }]
  const [uploadingImage, setUploadingImage] = useState(false)
  const [lightbox, setLightbox] = useState(null)        // { urls, index } | null
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [assignSaving, setAssignSaving] = useState(null) // 'team' | 'person' | null

  useEffect(() => { fetchTicket() }, [id])

  async function fetchTicket() {
    const [ticketRes, profilesRes, mentionableRes] = await Promise.all([
      supabase.from('tickets')
        .select('*, sites(id, name, city, state), programs(name, slug, color), profiles:assignee_id(full_name)')
        .eq('id', id)
        .single(),
      // People who could reasonably be assigned a ticket. Auditors are excluded
      // because their role is to surface issues, not work them; admin is included
      // because small teams sometimes need to self-assign.
      supabase.from('profiles')
        .select('id, full_name, role')
        .in('role', ['admin','executive','regional_ops','site_manager','field_tech','remote_tech'])
        .order('full_name'),
      // Everyone for the @mention autocomplete — auditors and any legacy
      // roles included. A ticket comment might legitimately need to loop
      // in the auditor who filed the finding.
      supabase.from('profiles')
        .select('id, full_name, role')
        .order('full_name'),
    ])
    setTicket(ticketRes.data)
    setProfiles(profilesRes.data || [])
    setMentionableProfiles(mentionableRes.data || [])

    // Load ticket images in parallel with signed URLs for display. Separate
    // query because there's no FK join convenience and we want ordering
    // control (oldest first so newly added images land at the end, matching
    // how chat / evidence timelines usually read).
    const { data: imgRows } = await supabase
      .from('ticket_images')
      .select('id, storage_path, uploaded_by, created_at')
      .eq('ticket_id', id)
      .order('created_at', { ascending: true })
    if (imgRows?.length) {
      const withUrls = await Promise.all(imgRows.map(async r => {
        const { data: u } = await supabase.storage.from('ticket-images').createSignedUrl(r.storage_path, 3600)
        return { ...r, url: u?.signedUrl || '' }
      }))
      setImages(withUrls)
    } else {
      setImages([])
    }

    setLoading(false)
  }

  async function uploadImage(file) {
    if (!file || !profile?.id) return
    if (!file.type.startsWith('image/')) { alert('Only image files are supported.'); return }

    setUploadingImage(true)
    try {
      // Path: first segment must be auth.uid() for storage RLS to accept the
      // write; remaining segments group by ticket so files stay organized.
      const ext = file.name.split('.').pop() || 'jpg'
      const path = `${profile.id}/${id}/${Date.now()}.${ext}`

      const { error: upErr } = await supabase.storage.from('ticket-images').upload(path, file)
      if (upErr) { alert('Upload failed: ' + upErr.message); return }

      const { data: inserted, error: insErr } = await supabase
        .from('ticket_images')
        .insert({ ticket_id: id, storage_path: path, uploaded_by: profile.id })
        .select('id, storage_path, uploaded_by, created_at')
        .single()
      if (insErr) {
        // Best-effort cleanup: if the row insert failed but the file uploaded,
        // remove the orphan so we don't pile up untracked storage objects.
        await supabase.storage.from('ticket-images').remove([path])
        alert('Upload failed: ' + insErr.message); return
      }

      // Append optimistically so the grid updates without a full refetch.
      const { data: u } = await supabase.storage.from('ticket-images').createSignedUrl(path, 3600)
      setImages(prev => [...prev, { ...inserted, url: u?.signedUrl || '' }])
    } finally {
      setUploadingImage(false)
    }
  }

  async function deleteImage(img) {
    if (!window.confirm('Delete this image?')) return
    // Best-effort: remove storage object, then metadata row. If the storage
    // call fails (e.g. already gone) we still try to clean the row so the UI
    // doesn't show phantom thumbnails.
    await supabase.storage.from('ticket-images').remove([img.storage_path])
    const { error } = await supabase.from('ticket_images').delete().eq('id', img.id)
    if (error) { alert('Delete failed: ' + error.message); return }
    setImages(prev => prev.filter(x => x.id !== img.id))
  }

  async function updateStatus(newStatus) {
    setSaving(true)
    const patch = { status: newStatus }
    if (newStatus === 'resolved') patch.resolved_at = new Date().toISOString()
    if (newStatus !== 'resolved') patch.resolved_at = null
    await supabase.from('tickets').update(patch).eq('id', id)
    await fetchTicket()
    setSaving(false)
  }

  async function setAssigneeTeam(team) {
    setAssignSaving('team')
    const { error } = await supabase.from('tickets').update({ assignee_team: team || null }).eq('id', id)
    if (error) alert('Save failed: ' + error.message)
    await fetchTicket()
    setAssignSaving(null)
  }

  async function setAssigneePerson(personId) {
    setAssignSaving('person')
    const { error } = await supabase.from('tickets').update({ assignee_id: personId || null }).eq('id', id)
    if (error) alert('Save failed: ' + error.message)
    await fetchTicket()
    setAssignSaving(null)
  }

  if (loading) return <div className="spinner" />
  if (!ticket) return <div className="empty-state"><h3>Ticket not found</h3></div>

  const ts = ticket.tier ? TIER_STYLE[ticket.tier] : null

  return (
    <div>
      <button className="btn btn-sm" style={{ marginBottom: 12 }} onClick={() => navigate(-1)}>← Back</button>

      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 12, fontFamily: 'monospace', color: 'var(--text-muted)', marginBottom: 4 }}>
          {ticket.ticket_number}
        </div>
        <div className="page-title">{ticket.title}</div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
          {ts && (
            <span style={{ fontSize: 11, padding: '3px 9px', borderRadius: 10, background: ts.bg, color: ts.color, fontWeight: 500 }}>
              {ts.dot} {ticket.tier}
            </span>
          )}
          {ticket.type && (
            <span style={{ fontSize: 11, padding: '3px 9px', borderRadius: 10, background: 'var(--gray-light)', color: 'var(--gray)' }}>
              {ticket.type}
            </span>
          )}
          {ticket.programs?.name && (
            <span style={{ fontSize: 11, padding: '3px 9px', borderRadius: 10, background: 'var(--blue-light)', color: 'var(--blue)' }}>
              {ticket.programs.name}
            </span>
          )}
        </div>
      </div>

      <div className="card" style={{ marginBottom: 12, cursor: ticket.sites?.id ? 'pointer' : 'default' }}
        onClick={() => ticket.sites?.id && navigate(`/sites/${ticket.sites.id}`)}>
        <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 6 }}>Site</div>
        <div style={{ fontWeight: 500, marginBottom: 4, textDecoration: ticket.sites?.id ? 'underline' : 'none' }}>
          {ticket.sites?.name}
        </div>
        <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
          {ticket.sites?.city}, {ticket.sites?.state}
        </div>
      </div>

      {ticket.description && (
        <div className="card" style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 6 }}>Description</div>
          <div style={{ fontSize: 14, lineHeight: 1.5 }}>{ticket.description}</div>
        </div>
      )}

      <div className="card" style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 8 }}>Status</div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {STATUSES.map(s => (
            <button
              key={s}
              disabled={saving}
              className={`btn btn-sm ${ticket.status === s ? 'btn-primary' : ''}`}
              onClick={() => updateStatus(s)}
              style={{ textTransform: 'capitalize' }}>
              {s.replace('_',' ')}
            </button>
          ))}
        </div>
      </div>

      <div className="card" style={{ marginBottom: 12 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>Assigned to</div>
          {assignSaving && (
            <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Saving…</div>
          )}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          <div>
            <label style={{ fontSize: 11, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>
              Team
            </label>
            <select
              className="form-input"
              value={ticket.assignee_team || ''}
              disabled={assignSaving === 'team'}
              onChange={e => setAssigneeTeam(e.target.value)}>
              <option value="">Unassigned</option>
              {TEAM_OPTIONS.map(t => (
                <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>
              ))}
            </select>
          </div>
          <div>
            <label style={{ fontSize: 11, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>
              Person (optional)
            </label>
            <select
              className="form-input"
              value={ticket.assignee_id || ''}
              disabled={assignSaving === 'person'}
              onChange={e => setAssigneePerson(e.target.value)}>
              <option value="">— None —</option>
              {profiles.map(p => (
                <option key={p.id} value={p.id}>
                  {p.full_name || p.id.slice(0,8)}{p.role ? ` (${p.role.replace(/_/g,' ')})` : ''}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Summary line — mirrors the logic Tickets list uses: person wins if set,
            else team, else Unassigned. */}
        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 8 }}>
          Currently in:{' '}
          <strong style={{ color: 'var(--text)' }}>
            {ticket.profiles?.full_name
              ? `${ticket.profiles.full_name}'s queue`
              : ticket.assignee_team
                ? `${ticket.assignee_team.replace(/_/g, ' ')} team queue`
                : 'No queue — needs triage'}
          </strong>
        </div>
      </div>

      {/* Images — anyone who can load this page can view/upload */}
      <div className="card" style={{ marginBottom: 12 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
          <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
            Images {images.length > 0 && <span>({images.length})</span>}
          </div>
          <label
            className="btn btn-sm btn-primary"
            style={{ cursor: uploadingImage ? 'wait' : 'pointer', opacity: uploadingImage ? 0.7 : 1 }}>
            {uploadingImage ? 'Uploading…' : '+ Add image'}
            <input
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              disabled={uploadingImage}
              onChange={e => {
                const f = e.target.files?.[0]
                // Clear the input so the same file can be re-picked after a
                // failed upload without having to reload the page.
                e.target.value = ''
                if (f) uploadImage(f)
              }}
            />
          </label>
        </div>

        {images.length === 0 ? (
          <div style={{ fontSize: 13, color: 'var(--text-muted)', padding: '8px 0' }}>
            No images attached yet. Use <strong>+ Add image</strong> to upload evidence, photos of parts, or screenshots.
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))', gap: 8 }}>
            {images.map((img, i) => {
              const canDelete = img.uploaded_by === profile?.id || profile?.role === 'admin'
              return (
                <div key={img.id} style={{ position: 'relative' }}>
                  <img
                    src={img.url}
                    alt=""
                    onClick={() => setLightbox({ urls: images.map(x => x.url), index: i })}
                    style={{
                      width: '100%', aspectRatio: '1', objectFit: 'cover',
                      borderRadius: 6, border: '1px solid var(--border)',
                      cursor: 'zoom-in', display: 'block',
                    }}
                  />
                  {canDelete && (
                    <button
                      onClick={() => deleteImage(img)}
                      aria-label="Delete image"
                      style={{
                        position: 'absolute', top: 4, right: 4,
                        width: 22, height: 22, borderRadius: 11,
                        background: 'rgba(0,0,0,0.65)', color: 'white',
                        border: 'none', fontSize: 13, lineHeight: 1,
                        cursor: 'pointer', display: 'flex',
                        alignItems: 'center', justifyContent: 'center',
                      }}>×</button>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </div>

      <div className="card" style={{ marginBottom: 12 }}>
        <TicketActivity
          ticketId={id}
          currentUserId={profile?.id}
          currentUserRole={profile?.role}
          profiles={mentionableProfiles}
        />
      </div>

      <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 24 }}>
        Created {new Date(ticket.created_at).toLocaleString()}
        {ticket.resolved_at && ` · Resolved ${new Date(ticket.resolved_at).toLocaleString()}`}
      </div>

      {lightbox && (
        <PhotoLightbox
          urls={lightbox.urls}
          index={lightbox.index}
          onClose={() => setLightbox(null)}
        />
      )}
    </div>
  )
}
